import { c as _c } from "react/compiler-runtime";
import type { Playbook, PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { Dialog } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActiveDeployment from './components/active-deployment';
import CreateDeploymentForm from './components/create-deployment-form';
import PastDeploymentsList from './components/past-deployments-list';
import useCreateDeployment from './use-create-deployment';
type VersionEntry = {
  versionId: string;
  weight: number;
};
type DeploymentDialogProps = {
  open: boolean;
  onClose: () => void;
  playbook: Playbook;
  playbookVersions: PlaybookVersionNoGraph[];
  defaultMode?: Mode;
  defaultVersions?: VersionEntry[];
};
type Mode = 'view' | 'create';
const DeploymentDialog = t0 => {
  const $ = _c(70);
  const {
    open,
    onClose,
    playbook,
    playbookVersions,
    defaultMode,
    defaultVersions
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "deployment.dialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const createDeployment = useCreateDeployment(playbook.id);
  const [mode, setMode] = useState(defaultMode ?? "view");
  const [currentVersions, setCurrentVersions] = useState(defaultVersions);
  const [isSameAsActive, setIsSameAsActive] = useState(false);
  let t2;
  let t3;
  if ($[1] !== defaultMode || $[2] !== defaultVersions || $[3] !== open) {
    t2 = () => {
      if (open) {
        setMode(defaultMode ?? "view");
        setCurrentVersions(defaultVersions);
      }
    };
    t3 = [open, defaultMode, defaultVersions];
    $[1] = defaultMode;
    $[2] = defaultVersions;
    $[3] = open;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== defaultMode || $[7] !== onClose) {
    t4 = () => {
      setMode(defaultMode ?? "view");
      onClose();
    };
    $[6] = defaultMode;
    $[7] = onClose;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleClose = t4;
  let t5;
  if ($[9] !== onClose) {
    t5 = () => {
      onClose();
    };
    $[9] = onClose;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleCreateSuccess = t5;
  if (mode === "create") {
    let t6;
    if ($[11] !== t) {
      t6 = t(_temp);
      $[11] = t;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t) {
      t7 = t(_temp2);
      $[13] = t;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== createDeployment.isPending || $[16] !== isSameAsActive || $[17] !== t7) {
      t8 = {
        form: "create-deployment-form",
        label: t7,
        type: "submit",
        disabled: isSameAsActive,
        loading: createDeployment.isPending
      };
      $[15] = createDeployment.isPending;
      $[16] = isSameAsActive;
      $[17] = t7;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== defaultMode || $[20] !== t) {
      t9 = defaultMode === "create" ? t(_temp3) : t(_temp4);
      $[19] = defaultMode;
      $[20] = t;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== defaultMode || $[23] !== handleClose) {
      t10 = defaultMode === "create" ? handleClose : () => setMode("view");
      $[22] = defaultMode;
      $[23] = handleClose;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== t10 || $[26] !== t9) {
      t11 = {
        label: t9,
        onClick: t10
      };
      $[25] = t10;
      $[26] = t9;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    let t12;
    if ($[28] !== playbook.activeDeployment?.routingPolicy.versions) {
      t12 = playbook.activeDeployment?.routingPolicy.versions.map(_temp5) ?? [];
      $[28] = playbook.activeDeployment?.routingPolicy.versions;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] !== playbook.activeDeployment?.routingPolicy.versions) {
      t13 = playbook.activeDeployment?.routingPolicy.versions ?? [];
      $[30] = playbook.activeDeployment?.routingPolicy.versions;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] !== createDeployment || $[33] !== playbook.activeDeployment?.id || $[34] !== playbook.id) {
      t14 = versions => createDeployment.mutateAsync({
        path: {
          playbookId: playbook.id
        },
        body: {
          expectedCurrentDeploymentId: playbook.activeDeployment?.id,
          routingPolicy: {
            kind: "weighted",
            versions
          }
        }
      });
      $[32] = createDeployment;
      $[33] = playbook.activeDeployment?.id;
      $[34] = playbook.id;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    let t15;
    if ($[36] !== currentVersions || $[37] !== handleCreateSuccess || $[38] !== playbookVersions || $[39] !== t12 || $[40] !== t13 || $[41] !== t14) {
      t15 = <div className="flex flex-col gap-6"><CreateDeploymentForm playbookVersions={playbookVersions} activeDeploymentVersionIds={t12} activeDeploymentVersions={t13} defaultVersions={currentVersions} onSuccess={handleCreateSuccess} onSameAsActiveChange={setIsSameAsActive} onDeploy={t14} /></div>;
      $[36] = currentVersions;
      $[37] = handleCreateSuccess;
      $[38] = playbookVersions;
      $[39] = t12;
      $[40] = t13;
      $[41] = t14;
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    let t16;
    if ($[43] !== handleClose || $[44] !== open || $[45] !== t11 || $[46] !== t15 || $[47] !== t6 || $[48] !== t8) {
      t16 = <Dialog open={open} onClose={handleClose} title={t6} isConfirmation={true} primaryButton={t8} secondaryButton={t11}>{t15}</Dialog>;
      $[43] = handleClose;
      $[44] = open;
      $[45] = t11;
      $[46] = t15;
      $[47] = t6;
      $[48] = t8;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    return t16;
  }
  let t6;
  if ($[50] !== t) {
    t6 = t(_temp6);
    $[50] = t;
    $[51] = t6;
  } else {
    t6 = $[51];
  }
  let t7;
  if ($[52] !== playbook.activeDeployment?.routingPolicy.versions) {
    t7 = () => {
      setCurrentVersions(playbook.activeDeployment?.routingPolicy.versions ?? []);
      setMode("create");
    };
    $[52] = playbook.activeDeployment?.routingPolicy.versions;
    $[53] = t7;
  } else {
    t7 = $[53];
  }
  let t8;
  if ($[54] !== playbook.activeDeployment || $[55] !== playbookVersions || $[56] !== t7) {
    t8 = <ActiveDeployment deployment={playbook.activeDeployment} playbookVersions={playbookVersions} onEdit={t7} />;
    $[54] = playbook.activeDeployment;
    $[55] = playbookVersions;
    $[56] = t7;
    $[57] = t8;
  } else {
    t8 = $[57];
  }
  const t9 = playbook.activeDeployment?.id;
  let t10;
  if ($[58] !== playbook.id || $[59] !== playbookVersions || $[60] !== t9) {
    t10 = <PastDeploymentsList playbookId={playbook.id} playbookVersions={playbookVersions} activeDeploymentId={t9} />;
    $[58] = playbook.id;
    $[59] = playbookVersions;
    $[60] = t9;
    $[61] = t10;
  } else {
    t10 = $[61];
  }
  let t11;
  if ($[62] !== t10 || $[63] !== t8) {
    t11 = <div className="flex flex-col gap-6">{t8}{t10}</div>;
    $[62] = t10;
    $[63] = t8;
    $[64] = t11;
  } else {
    t11 = $[64];
  }
  let t12;
  if ($[65] !== handleClose || $[66] !== open || $[67] !== t11 || $[68] !== t6) {
    t12 = <Dialog open={open} onClose={handleClose} title={t6} isConfirmation={true}>{t11}</Dialog>;
    $[65] = handleClose;
    $[66] = open;
    $[67] = t11;
    $[68] = t6;
    $[69] = t12;
  } else {
    t12 = $[69];
  }
  return t12;
};
export default DeploymentDialog;
function _temp($) {
  return $.createTitle;
}
function _temp2($_0) {
  return $_0.create;
}
function _temp3($_1) {
  return $_1.cancel;
}
function _temp4($_2) {
  return $_2.back;
}
function _temp5(v) {
  return v.versionId;
}
function _temp6($_3) {
  return $_3.title;
}